import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { effectiveCourtHidden } from '@/lib/courtVisibility';

interface GroupMatch {
  id: string;
  time: string;
  match: string;
  date?: string;
  court?: string;
  courtHidden?: boolean;
}

interface GroupParticipant {
  id: string;
  letter: string;
  player1: string;
  player2?: string;
}

interface GroupInfo {
  groupName: string;
  category: string;
  subcategory?: string;
  participantCount: number;
  participants: GroupParticipant[];
  matches: GroupMatch[];
  scheduleInfo?: {
    date: string;
    startTime: string;
    endTime: string;
    court: string;
    courtHidden?: boolean;
  };
}

export function useAllTournamentGroups(eventId: string) {
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchGroups() {
      try {
        setLoading(true);
        
        // Fetch registrations
        const { data: registrations, error: regError } = await supabase
          .from('registrations')
          .select('id, group, category, subcategory, player1_name, player2_name')
          .eq('event_id', eventId)
          .eq('status', 'confirmed')
          .not('group', 'is', null);

        if (regError) throw regError;

        // Fetch tournament schedule
        const { data: scheduleData, error: scheduleError } = await supabase
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', eventId)
          .single();

        if (scheduleError && scheduleError.code !== 'PGRST116') {
          console.warn('[useAllTournamentGroups] Schedule not found:', scheduleError);
        }

        // Group by group name and process data
        const groupMap = new Map<string, GroupInfo>();
        
        registrations?.forEach(reg => {
          const key = `${reg.group}-${reg.category}-${reg.subcategory || ''}`;
          
          const participant: GroupParticipant = {
            id: reg.id,
            letter: '', // Will be assigned based on order
            player1: reg.player1_name || 'Jugador 1',
            player2: reg.player2_name || undefined
          };

          if (groupMap.has(key)) {
            const existing = groupMap.get(key)!;
            groupMap.set(key, {
              ...existing,
              participantCount: existing.participantCount + 1,
              participants: [...existing.participants, participant]
            });
          } else {
            groupMap.set(key, {
              groupName: reg.group,
              category: reg.category || '',
              subcategory: reg.subcategory,
              participantCount: 1,
              participants: [participant],
              matches: []
            });
          }
        });

        // Assign letters to participants
        groupMap.forEach((group) => {
          group.participants.forEach((participant, index) => {
            participant.letter = String.fromCharCode(65 + index); // A, B, C...
          });
        });

        // Parse schedule data to extract matches
        if (scheduleData?.schedule_data) {
          const schedule = scheduleData.schedule_data as any;
          // Visibilidad de canchas (organizador). Ver src/lib/courtVisibility.ts
          const globalHideCourts = schedule._meta?.hideCourtsFromParticipants === true;

          if (schedule.groupSchedule && Array.isArray(schedule.groupSchedule)) {
            schedule.groupSchedule.forEach((block: any) => {
              const key = `${block.group}-${block.category}-${block.subcategory || ''}`;
              const groupInfo = groupMap.get(key);

              if (groupInfo && block.matches && Array.isArray(block.matches)) {
                const courtHidden = effectiveCourtHidden(block.courtHidden, globalHideCourts);
                // Si el organizador ocultó la cancha, no la exponemos al participante.
                const visibleCourt = courtHidden ? '' : (block.court || '');

                // Set schedule info
                groupInfo.scheduleInfo = {
                  date: block.date || '',
                  startTime: block.startTime || '',
                  endTime: block.endTime || '',
                  court: visibleCourt,
                  courtHidden,
                };

                // Extract matches
                groupInfo.matches = block.matches.map((match: any, index: number) => ({
                  id: `${block.group}-${index}`,
                  time: match.time || '',
                  match: match.match || '',
                  date: block.date,
                  court: visibleCourt || undefined,
                  courtHidden,
                }));
              }
            });
          }
        }

        const groupsArray = Array.from(groupMap.values()).sort((a, b) => 
          a.groupName.localeCompare(b.groupName)
        );

        setGroups(groupsArray);
        setError(null);
      } catch (err) {
        console.error('[useAllTournamentGroups] Error:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar grupos');
      } finally {
        setLoading(false);
      }
    }

    fetchGroups();
  }, [eventId]);

  return { groups, loading, error };
}
